import {
  ILanguages,
  IListingPhotos,
  IObjectsHasTags,
} from '@llws/lift-entity-interfaces';
import { IApiCommandResponse } from '@llws/api-common';
import { IQueryCommandOptions, Equal } from '@llws/dynamic-router-utils';
import {
  IApiResponse, IEditImage, IModifyPayload,
} from '@/shared';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { getNewListingPhoto } from '@/shared/factory/new-listing-photo';
import uploads from '@/shared/services/uploads';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { INewTranslationSettingsStoreConfig } from '@/shared/interfaces/new-translation-settings';
import { getByModelAndObjectId } from '@/shared/services/tags';
import { IFilterPayloadV2 } from '@/shared/interfaces/filter-payload-v2';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/property-api/properties/:param/listing-photos';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  limit: 999,
  orderBy: [{ field: 'orderBy', direction: 'asc' }],
  where: [Equal('active', '1')],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};
const translationSettings: INewTranslationSettingsStoreConfig = {
  model: 'photos',
  keys: ['name', 'alt_text'],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IListingPhotos>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
  translationSettings,
});

export interface IListingPhotosWithExtraData extends IListingPhotos {
  languages: ILanguages[];
  objectsHasTags: IObjectsHasTags[];
}

export interface ICustomListingPhotoState extends IStateStrict<IListingPhotos> {
  singleWithExtraData: IListingPhotosWithExtraData | null;
}

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'listing-photos/'

  state: {
    ...base.state,
    singleWithExtraData: null,
  } as ICustomListingPhotoState,

  getters: {
    ...base.getters,
    singleWithExtraData: (state: ICustomListingPhotoState) => state.singleWithExtraData,
  },

  mutations: {
    ...base.mutations,

    setSingleWithExtraData: (state: ICustomListingPhotoState, payload: IListingPhotosWithExtraData | null) => {
      state.singleWithExtraData = payload;
    },
  },

  actions: {
    ...base.actions,

    deleteVideo: async (
      ctx: IAction<IStateStrict<IListingPhotos>>,
      payload: IModifyPayload<number, number>,
    ): Promise<boolean> => {
      // Save it to the database
      const deletePhoto: IApiCommandResponse<IListingPhotos| undefined> = await adminRequest.delete(
        `/property-api/properties/${payload.parentId}/listing-photos`,
        [payload.id || -1],
      );
      return !!deletePhoto;
    },

    getWithTagsAndTranslations: async (
      ctx: IAction<IStateStrict<IListingPhotos>>,
      payload: IFilterPayloadV2<number>,
    ) => {
      await ctx.dispatch('get', {
        ...payload,
        ...{ additional: { includeTranslations: true } },
      } as IFilterPayloadV2<number>);

      const tagsResponse = await getByModelAndObjectId('building-photos', ctx.getters.single.id);
      ctx.commit('setSingleWithExtraData', { ...ctx.getters.single, ...{ objectsHasTags: tagsResponse.data || [] } });
    },

    uploadAndSave: async (
      ctx: IAction<IStateStrict<IListingPhotos>>,
      payload: IModifyPayload<number, File, { clientKey: string, clientId: number }>,
    ) => {
      const uploadResult = await uploads.uploadSingleFile(
        payload.additional!.clientKey,
        'gallery',
        payload.data,
      );
      const image = uploadResult.filename.split('/').pop()!;

      const listingPhotoResponse: IApiResponse<IListingPhotos[], null> = await adminRequest.post(
        `/property-api/properties/${payload.parentId}/listing-photos`,
        getNewListingPhoto(payload.additional!.clientId, payload.parentId!, image, uploadResult.height, uploadResult.width),
      );

      ctx.commit('set', [ ...ctx.getters.list, listingPhotoResponse.data![0]! ]);
      return listingPhotoResponse.data![0]!;
    },

    saveUploadedImageEdits: async (
      ctx: IAction<IStateStrict<IListingPhotos>>,
      payload: IModifyPayload<number, IListingPhotos, { clientKey: string, filename: string, imageEdits: IEditImage[] }>,
    ): Promise<IListingPhotos> => {
      const uploadEditResult = await uploads.updateImageAsNew(
        payload.additional!.clientKey,
        payload.additional!.filename,
        'gallery',
        payload.additional!.imageEdits,
      );

      const updatedPhoto: IApiResponse<IListingPhotos, null> = await adminRequest.put(
        `/property-api/properties/${payload.parentId}/listing-photos/${payload.id}`,
        {
          clientId: payload.data.clientId,
          image: uploadEditResult.filename.split('/').pop(),
          ...!payload.data.originalImage && { originalImage: payload.data.image },
        },
      );

      const newPhoto = { ...ctx.getters.single, ...updatedPhoto.data! };
      ctx.commit('set', newPhoto);
      return newPhoto;
    },

    resetToOriginalImage: async (
      ctx: IAction<IStateStrict<IListingPhotos>>,
      payload: IModifyPayload<number, IListingPhotos>,
    ): Promise<IListingPhotos> => {
      const updatedPhoto: IApiResponse<IListingPhotos, null> = await adminRequest.put(
        `/property-api/properties/${payload.parentId}/listing-photos/${payload.id}`,
        {
          image: payload.data.originalImage,
          clientId: payload.data.clientId,
        },
      );

      const newPhoto = { ...ctx.getters.single, ...updatedPhoto.data! };
      ctx.commit('set', newPhoto);
      return newPhoto;
    },
  },
};
